import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { PropertyInfoPriceGuaranteeViewModel, ScrollTarget, StickyOfferModel } from 'models';
import { VrPropertySummaryViewModel } from 'models/hotel/hotel-details-types';

import { UitkPrimaryButton } from 'uitk-react-button';
import { UitkRating, UitkRatingIcon } from 'uitk-react-rating';

import { PropertySummary } from 'components/infosite/property-details/property-summary';
import { QuickReserveButton } from 'components/infosite/rooms-and-rates/quick-reserve-button';

export interface InformationContentProps {
  name: string;
  englishName: string;
  stars?: string;
  starRatingIcon?: string;
  singleUnitVrPropertySummary?: VrPropertySummaryViewModel;
  priceGuarantee?: PropertyInfoPriceGuaranteeViewModel;
  withPrice?: boolean;
  propertyAffinities?: string;
  navigateTabs?: () => any;
}

export interface InformationOfferProps {
  stickyOffer?: StickyOfferModel;
  target?: ScrollTarget;
  currentHotel?: CurrentHotelStore;
}

export type InformationProps = InformationContentProps & InformationOfferProps;

export const Information: React.FunctionComponent<InformationProps> = (props: InformationProps) => <InformationSmall {...props} />;

const ButtonTracked = ClickTracker(UitkPrimaryButton);

export const InformationLarge: React.FunctionComponent<InformationContentProps & InformationOfferProps> = props => (
  <div className="uitk-grid">
    <div className="uitk-cell all-cell-fill">
      <InformationSmall {...props} />
    </div>
    {Boolean(props.navigateTabs) && (
      <Viewport>
        <ViewSmall />
        <ViewMedium>
          <div className="uitk-cell all-cell-shrink all-l-padding-three">
            <div className="uitk-grid all-grid-align-middle uitk-type-right all-cell-1-4">
              <ErrorBoundary label="hotel.information.details.leadprice.large">
                <ButtonTracked onClick={props.navigateTabs} className="book-now-tabbed" rfrr=".GoToReserveTab" linkName="Go To Reserve">
                  <LocalizedText message="hotels.infosite.stickyBookNow.priceDisplay.bookButton" />
                </ButtonTracked>
              </ErrorBoundary>
            </div>
          </div>
        </ViewMedium>
      </Viewport>
    )}
    {!props.navigateTabs && props.stickyOffer && (
      <div className="uitk-cell all-cell-shrink all-l-padding-three">
        <div className="uitk-grid all-grid-align-middle uitk-type-right all-cell-1-4">
          <ErrorBoundary label="hotel.information.details.leadprice.large">
            <QuickReserveButton
              {...props.stickyOffer}
              buttonMessage={props.stickyOffer.buttonText}
              target={props.target}
              isPriceRightAligned={true}
              withPrice={props.withPrice}
            />
          </ErrorBoundary>
        </div>
      </div>
    )}
  </div>
);

export const InformationSmall: React.FunctionComponent<InformationContentProps & InformationOfferProps> = ({
  name,
  englishName,
  singleUnitVrPropertySummary,
  starRatingIcon,
  stars,
  priceGuarantee,
  currentHotel,
  propertyAffinities
}) => {
  const fillCellClass = 'uitk-cell all-cell-fill';

  const starRating = (
    <>
      <span className="is-visually-hidden">
        <LocalizedText message={'hotels.searchResults.filtersDialog.starRating.label'} />
      </span>
      <UitkRating icon={UitkRatingIcon[starRatingIcon]} rating={stars} />
    </>
  );

  return (
    <div className="uitk-grid all-cell-1-1">
      <div className="uitk-cell all-cell-fill">
        <h1 className="uitk-cell all-cell-shrink uitk-type-heading-600 all-b-padding-half" data-stid="content-hotel-title">
          {name}
          {propertyAffinities && (
            <span className="uitk-cell all-l-padding-one" data-stid="property-star-rating">
              {starRating}
            </span>
          )}
        </h1>
        {englishName && currentHotel && currentHotel.searchCriteria.isDateless && (
          <h2 className="uitk-cell all-cell-shrink uitk-type-300 uitk-type-regular" data-stid="content-english-hotel-title">
            {englishName}
          </h2>
        )}
        <meta itemProp="name" content={englishName ? `${name} ${englishName}` : name} />
      </div>
      {singleUnitVrPropertySummary && <PropertySummary singleUnitVrPropertySummary={singleUnitVrPropertySummary} />}
      {propertyAffinities}
      {!propertyAffinities && (
        <div className={fillCellClass} data-stid="property-star-rating">
          {starRating}
        </div>
      )}
    </div>
  );
};

InformationSmall['displayName'] = 'InformationSmall';
